import "./style.css";
import {
  LinearProgress,
  Stack,
  Button,
  Tooltip,
  IconButton,
  Icon,
} from "@mui/material";
import MDDataGrid from "components/MDDataGrid/MDDataGrid";
import MDTypography from "components/MDTypography";
import { centsToDollars } from "global/functions";
import MDBadge from "components/MDBadge";
import { useMaterialUIController } from "context";
import MDBox from "components/MDBox";
import EmptyTableRowOverlay from "components/MDEmptyOverlay/EmptyTableRowOverlay";
import { useEffect, useMemo, useState } from "react";
import CircularIndeterminate from "components/MDLoading";
import MDSnackbar from "components/MDSnackbar";
import { mobileNumberMasking } from "global/functions";
import LicensePlatesDialog from "./LicensePlatesDialog";
import { debounce, get, isEmpty } from "lodash";
import { formatDateWithTimezone } from "global/functions";
import { capitalizeFirstLetter } from "global/functions";
import { getTransactionsByPlaceId } from "store/slice/transactions/transactionSlice";
import { useDispatch, useSelector } from "react-redux";
import RetryTransferDialog from "./RetryTransferDialog";
import { clearRetryState } from "store/slice/transactions/transactionSlice";

const TransactionsTable = (props) => {
  const [retryDialogOpen, setRetryDialogOpen] = useState(false);
  const [retryTransferData, setRetryTransferData] = useState(null);
  const [licensePlateView, setLicensePlateView] = useState(false);
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(0);
  const [sortModel, setSortModel] = useState([]);
  const [licensePlates, setLicensePlates] = useState([]);

  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;
  const dispatch = useDispatch();
  useState(false);
  const { placeId, startDate, endDate, searchText, tz, activeTab } = props;
  const transactionsList = useSelector((state) => state.transactions?.list);
  const totalTransactions = useSelector(
    (state) => state.transactions?.totalTransactions,
  );
  const loading = useSelector((state) => state.transactions?.loading);

  const [notification, setNotification] = useState({
    color: "",
    icon: "",
    title: "",
    content: "",
    show: false,
    close: () => setNotification({ ...notification, show: false }),
  });

  const [filterModel, setFilterModel] = useState({ items: [] });
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    JSON.parse(localStorage.getItem("transactionalDataGridColumnVisibility")) ||
      {},
  );
  const [columnOrder, setColumnOrder] = useState(
    JSON.parse(localStorage.getItem("transactionalDataGridColumnOrder")) || [],
  );

 
  const handleColumnVisibilityChange = (newColumnVisibilityModel) => {
    setColumnVisibilityModel(newColumnVisibilityModel);
    localStorage.setItem(
      "transactionalDataGridColumnVisibility",
      JSON.stringify(newColumnVisibilityModel),
    );
  };

  const handleColumnOrderChange = (newColumnOrder) => {
    setColumnOrder(newColumnOrder);
    localStorage.setItem(
      "transactionalDataGridColumnOrder",
      JSON.stringify(newColumnOrder),
    );
  };

  const handleDialogClose = () => {
    setRetryDialogOpen(false);
    dispatch(clearRetryState());
  };

  useEffect(() => {
    const savedVisibility = JSON.parse(localStorage.getItem("transactionalDataGridColumnVisibility"));
  if (savedVisibility) setColumnVisibilityModel(savedVisibility);

  const savedOrder = JSON.parse(localStorage.getItem("transactionalDataGridColumnOrder"));
  if (savedOrder) setColumnOrder(savedOrder);
}, []);


  const columnsData = [
    {
      field: "transientNumber",
      headerName: "ID",
      width: 120,
      editable: false,
      renderCell: ({ row }) => {
        let color;
        switch (row.paymentStatus) {
          case "failed":
            color = "red";
            break;
          case "refunded":
            color = "#1b6be4";
            break;
        }

        return (
          <MDTypography display="block" variant="caption" style={{ color }}>
            {row.purpose === "PARKING"
              ? row.transientNumber || "-"
              : row.subscriptionNumber || "-"}
          </MDTypography>
        );
      },
    },

    {
      field: "transactionDate",
      headerName: "Transaction Date",
      width: 150,
      type: "date",
      editable: false,
      valueGetter: (params) => {
        if (!params || !params.row) return null;

        const row = params.row;
        const value =
          row.paymentStatus === "refunded"
            ? get(row, "refundDate", row.transactionDate)
            : row.transactionDate||"_";

        return value ? new Date(value) : null;
      },
      renderCell: ({ row }) => (
        <MDTypography
          display="block"
          variant="caption"
          sx={{ color: "black.light" }}
        >
          {` ${
            formatDateWithTimezone(
              row.paymentStatus === "refunded"
                ? get(row, "refundDate", row.transactionDate)
                : row.transactionDate,
              row?.placeId?.timeZoneId,
            ) || "-"
          }`}
        </MDTypography>
      ),
    },
    {
      field: "transactionId",
      headerName: "Transaction ID",
      width: 250,
      editable: false,
      renderCell: ({ row }) => (
        <MDTypography
          display="block"
          variant="caption"
          sx={{ color: "black.light" }}
        >
          {`${
            row.paymentStatus === "refunded"
              ? get(row, "refundId", get(row, "transactionId", "-"))
              : get(
                  row,
                  "paymentInfo.transactionResponse.transId",
                  get(row, "transactionId", "-"),
                )
          }`}
        </MDTypography>
      ),
    },

    {
      field: "purpose",
      headerName: "Transaction For",
      width: 150,
      editable: false,
      renderCell: ({ row }) => (
        <MDTypography
          display="block"
          variant="caption"
          sx={{ color: "black.light" }}
        >
          {` ${row.purpose || "-"}`}
        </MDTypography>
      ),
    },

    {
      field: "licensePlate",
      headerName: "License Plates",
      width: 170,
      editable: false,
      renderCell: ({ row }) => (
        <>
          <div>
            <MDBadge
              badgeContent={row.licensePlate[0]?.licensePlateNumber}
              color={sidenavColor}
              variant="gradient"
              size="sm"
              sx={{ marginRight: 1 }}
            />
            {row.licensePlate.length > 1 && (
              <Button
                color={sidenavColor}
                sx={{ padding: 0 }}
                disableRipple
                onClick={() => {
                  setLicensePlateView(true);
                  setLicensePlates(row.licensePlate);
                }}
              >
                ...{row.licensePlate.length - 1} more
              </Button>
            )}
          </div>
        </>
      ),
    },
    {
      field: "parkerName",
      headerName: "Parker Name",
      width: 180,
      editable: false,
      renderCell: ({ row }) => (
        <MDTypography
          display="block"
          variant="caption"
          sx={{ color: "black.light" }}
        >
          {` ${row?.parkerName || "-"}`}
        </MDTypography>
      ),
    },
    {
      field: "parkerEmail",
      headerName: "Parker Email",
      width: 180,
      editable: false,
      renderCell: ({ row }) => (
        <MDTypography
          display="block"
          variant="caption"
          sx={{ color: "black.light" }}
        >
          {` ${row?.parkerEmail||row.customerId?.email || "-"}`}
        </MDTypography>
      ),
    },
    {

      field: "cellPhone",
      headerName: "Cell Phone",
      width: 150,
      // type:"number",
      editable: false,
      renderCell: ({ row }) => (
        <MDTypography
          display="block"
          variant="caption"
          sx={{ color: "black.light" }}
        >
          {` ${
            row.customerId?.isEmailPrimary
              ? row.customerId?.secondaryMobile
                ? mobileNumberMasking(row.customerId.secondaryMobile)
                : "-"
              : row.customerId?.mobile
                ? mobileNumberMasking(row.customerId.mobile)
                : "-"
          }`}
        </MDTypography>
      ),
    },

    {
      field: "name",
      headerName: "Name",
      width: 120,
      editable: false,
      valueGetter: (params) => {
        if (!params || !params.row) return "";
        const first = params.row.customerId?.firstName || "";
        const last = params.row.customerId?.lastName || "";
        return `${first} ${last}`.trim();
      },
      renderCell: ({ row }) => (
        <MDTypography
          display="block"
          variant="caption"
          sx={{ color: "black.light" }}
        >
          {` ${row.customerId?.firstName || "-"} ${
            row.customerId?.lastName || "-"
          }`}
        </MDTypography>
      ),
    },

    {
      field: "subscriptionType",
      headerName: "Subscription Type",
      width: 120,
      editable: false,
      renderCell: ({ row }) => (
        <MDTypography
          display="block"
          variant="caption"
          sx={{ color: "black.light" }}
        >
          {row.isMonthly ? "Monthly" : "Custom"}
        </MDTypography>
      ),
    },
    {
      field: "paymentMethodType",
      headerName: "Payment Method",
      width: 120,
      editable: false,
      renderCell: ({ row }) => (
        <MDTypography
          display="block"
          variant="caption"
          sx={{ color: "black.light" }}
        >
          {row.paymentMethodType === "card"
            ? "Credit card"
            : row.paymentMethodType}
        </MDTypography>
      ),
    },

    {
      field: "paymentGatewayFeePayBy",
      headerName: "Payment Gateway Fee Pay By",
      width: 150,
      editable: false,
      renderCell: ({ row }) => (
        <MDTypography
          display="block"
          variant="caption"
          sx={{ color: "black.light" }}
        >
          {capitalizeFirstLetter(row.paymentGatewayFeePayBy) || "-"}
        </MDTypography>
      ),
    },

    {
      field: "baseRate",
      headerName: "Base Rate",
      width: 120,
      editable: false,
      type: "number",
      renderCell: ({ row }) => (
        <MDTypography
          display="block"
          variant="caption"
          sx={{ color: "black.light" }}
        >
          {`$${centsToDollars(row?.baseRate)}`}
        </MDTypography>
      ),
    },

    {
      field: "tax",
      headerName: "Tax",
      width: 120,
      editable: false,
      type: "number",
      renderCell: ({ row }) => (
        <MDTypography
          display="block"
          variant="caption"
          sx={{ color: "black.light" }}
        >
          {`$${centsToDollars(row?.tax)}`}
        </MDTypography>
      ),
    },

    {
      field: "cityTax",
      headerName: "City Tax",
      width: 120,
      editable: false,
      type: "number",
      renderCell: ({ row }) => (
        <MDTypography
          display="block"
          variant="caption"
          sx={{ color: "black.light" }}
        >
          {`$${centsToDollars(row?.cityTax)}`}
        </MDTypography>
      ),
    },

    {
      field: "countyTax",
      headerName: "County Tax",
      width: 120,
      editable: false,
      type: "number",
      renderCell: ({ row }) => (
        <MDTypography
          display="block"
          variant="caption"
          sx={{ color: "black.light" }}
        >
          {`$${centsToDollars(row?.countyTax)}`}
        </MDTypography>
      ),
    },

    {
      field: "serviceFee",
      headerName: "Service Fee",
      width: 100,
      editable: false,
      type: "number",
      renderCell: ({ row }) => (
        <>
          <MDTypography
            display="block"
            variant="caption"
            sx={{ color: "black.light" }}
          >
            {row.isApplyServiceFee ? `$${centsToDollars(row.serviceFee)}` : 0}
          </MDTypography>
        </>
      ),
    },
    {
      field: "paymentGatewayFee",
      headerName: "Payment Gateway Fee",
      width: 150,
      editable: false,
      type: "number",
      renderCell: ({ row }) => (
        <MDTypography
          display="block"
          variant="caption"
          sx={{ color: "black.light" }}
        >
          {`$${centsToDollars(row?.paymentGatewayFee)}`}
        </MDTypography>
      ),
    },
    {
      field: "totalAmount",
      headerName: "Total Amount",
      width: 150,
      editable: false,
      type: "number",
      renderCell: ({ row }) => (
        <MDTypography
          display="block"
          variant="caption"
          sx={{ color: "black.light" }}
        >
          {`$${centsToDollars(row?.totalAmount)}`}
        </MDTypography>
      ),
    },
    {
      field: "ownerPayout",
      headerName: "Owner Payout",
      width: 100,
      editable: false,
      type:"number",
      renderCell: ({ row }) => (
        <MDTypography
          display="block"
          variant="caption"
          sx={{ color: "black.light" }}
        >
          {`$${centsToDollars(row?.ownerPayout)}`}
        </MDTypography>
      ),
    },
    {
      field: "primaryConnectAccountPayout",
      headerName: "Client Payout",
      width: 100,
      editable: false,
      type:"number",
      renderCell: ({ row }) => (
        <>
          {(row?.paymentInfo?.primaryConnectAccountPayout ||
            row?.primaryConnectAccountPayout) && (
            <MDTypography
              display="block"
              variant="caption"
              sx={{ color: "black.light" }}
            >
              {`$${centsToDollars(
                row?.primaryConnectAccountPayout ||
                  row?.paymentInfo?.primaryConnectAccountPayout,
              )}`}
            </MDTypography>
          )}
        </>
      ),
    },
    {
      field: "secondaryConnectAccountPayout",
      headerName: "Operator Payout",
      width: 100,
      editable: false,
        type:"number",
      renderCell: ({ row }) => (
        <>
          {(row?.paymentInfo?.secondaryConnectAccountPayout ||
            row?.secondaryConnectAccountPayout) && (
            <MDTypography
              display="block"
              variant="caption"
              sx={{ color: "black.dark" }}
            >
              {`$ ${centsToDollars(
                row?.secondaryConnectAccountPayout ||
                  row?.paymentInfo?.secondaryConnectAccountPayout,
              )}`}
            </MDTypography>
          )}
        </>
      ),
    },
    {
      field: "paymentStatus",
      headerName: "Transaction Status",
      width: 120,
      editable: false,
      // type:"boolean",
      renderCell: ({ row }) => (
        <MDTypography
          display="block"
          variant="caption"
          sx={{ color: "black.light" }}
        >
          {capitalizeFirstLetter(row?.paymentStatus)}
        </MDTypography>
      ),
    },

    {
      field: "reason",
      headerName: "Failed Reason",
      width: 220,
      editable: false,
      filterable:false,
      renderCell: ({ row }) => {
        if (row.paymentStatus === "failed") {
          return (
            <MDTypography
              display="block"
              variant="caption"
              sx={{ color: "black.light" }}
            >
              {get(
                row,
                "paymentInfo.transactionResponse.errors[0].errorText",
                get(row, "paymentInfo.message", "-"),
              )}
            </MDTypography>
          );
        }
        return (
          <MDTypography
            display="block"
            variant="caption"
            sx={{ color: "black.light" }}
          >
            -
          </MDTypography>
        );
      },
    },
    {
      field: "transferStatus",
      headerName: "Transfer Status",
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => {
        const extra = get(row, "paymentInfo.extraParams", {});
        const isSplit = get(row, "isPaymentSplit", false);
        const initialized = row.paymentStatus === "initialize";
        const primaryFailed = extra.primaryTransfer?.success === false;
        const secondaryFailed = extra.secondaryTransfer?.success === false;

        const allGood =
          !primaryFailed &&
          !secondaryFailed &&
          row.paymentStatus === "success" &&
          extra.primaryTransfer &&
          extra.secondaryTransfer;

        return (
          <Tooltip
            title={
              allGood ? "Transfers succeeded" : "One or more transfers failed"
            }
          >
            <MDTypography
              display="block"
              variant="caption"
              sx={{ color: "black.light" }}
            >
              {!isSplit
                ? "N/A"
                : initialized
                  ? "Pending"
                  : allGood
                    ? "Success"
                    : "Failed"}
            </MDTypography>
          </Tooltip>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      filterable:false,
      renderCell: ({ row }) => {
        const extra = get(row, "paymentInfo.extraParams", {});
        const allowReTransfer =
          extra.primaryTransfer &&
          extra.secondaryTransfer &&
          row.paymentStatus !== "refunded" &&
          row.paymentStatus !== "failed" &&
          (extra.primaryTransfer.success === false ||
            extra.secondaryTransfer.success === false);
        return (
          <Stack direction="row" alignItems="center">
            {row.paymentStatus !== "failed" && (
              <Tooltip title="Download Receipt" placement="top">
                <IconButton
                  color="success"
                  onClick={() => {
                    if (row?.receiptURL) {
                      window.open(row?.receiptURL, "_blank");
                    }
                  }}
                >
                  <Icon fontSize="small">download </Icon>
                </IconButton>
              </Tooltip>
            )}

            {allowReTransfer && (
              <Tooltip title="Retry Transfer" placement="top">
                <IconButton
                  sx={{ mx: 1 }}
                  color="info"
                  onClick={() => {
                    setRetryTransferData(row);
                    setRetryDialogOpen(true);
                  }}
                  size="small"
                >
                  <Icon fontSize="small">currency_exchange</Icon>
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        );
      },
    },
  ];

  const getColumnsByTransactionType = (transactionType) => {
    if (transactionType === "refunded") {
      return columnsData.filter((column) =>
        [
          "transactionId",
          "transactionDate",
          "purpose",
          "subscriptionNumber",
          "licensePlate",
          "totalAmount",
        ].includes(column.field),
      );
    }
    return columnsData;
  };

  const getTransactionsData = async (
    pageNo,
    currentFilterModel = filterModel,
    currentSortModel = sortModel,
  ) => {
    const payload = {
      tz,
      pageNo: pageNo || 0,
      status: activeTab === "pendingPayment" ? "initialize" : activeTab,
      placeId,
      search: searchText,
      startDate,
      endDate,
      filterModel: currentFilterModel,
      sortModel: currentSortModel,
    };
    dispatch(getTransactionsByPlaceId(payload));
  };

  const debounceFn = useMemo(
    () => debounce(getTransactionsData, 1000),
    [getTransactionsData, filterModel, sortModel],
  );
  // 3. UPDATE: Trigger API on filter change & reset page to 0
  const handleFilterChange = (newFilterModel) => {
    setFilterModel(newFilterModel);
    localStorage.setItem(
      "transactionalDataGridFilters",
      JSON.stringify(newFilterModel.items),
    );
    getTransactionsData(0, newFilterModel, sortModel);
    setPage(0);
  };

  // 4. ADD: Handle Sort Change
  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    getTransactionsData(0, filterModel, newSortModel);
    setPage(0);
  };

  useEffect(() => {
    debounceFn();
  }, [startDate, endDate, searchText, placeId]);

  const handlePageChange = (params) => {
    getTransactionsData(params.page);
    setPage(params.page);
  };

  const handlePageSizeChange = (params) => {
    setPageSize(params.pageSize);
  };

  return (
    <>
      {console.log("Transactions List >>>>>>>>>>>>>>>>", transactionsList)}
      <MDBox mt={2}>
        <MDDataGrid
          rows={!isEmpty(transactionsList) ? transactionsList : []}
          rowCount={totalTransactions}
          columns={getColumnsByTransactionType(activeTab)}
          getRowId={(row) => row._id}
          disableSelectionOnClick
          loading={loading}
          pagination
          paginationMode="server"
          paginationModel={{ page: page, pageSize: pageSize }}
          onPaginationModelChange={(params) => {
            handlePageChange(params);
          }}
          // getRowClassName={(params) => getRowClassName(params)}
          onPageSizeChange={(params) => handlePageSizeChange(params)}
          components={{
            NoRowsOverlay: EmptyTableRowOverlay,
            LoadingOverlay: LinearProgress,
          }}
          componentsProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 1000 },
            },
          }}
          // autoHeight
          filterMode="server"
          sortingMode="server"
          filterModel={filterModel}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          onFilterModelChange={handleFilterChange}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={handleColumnVisibilityChange}
          columnOrder={columnOrder}
          onColumnOrderChange={handleColumnOrderChange}
        />
      </MDBox>
      <LicensePlatesDialog
        licensePlates={licensePlates}
        dialogOpen={licensePlateView}
        onClose={() => {
          setLicensePlateView(false);
        }}
      />
      <CircularIndeterminate
        type="full"
        size={20}
        text="Please wait..."
        open={loading}
      />
      <MDSnackbar
        color={notification.color}
        icon={notification.icon}
        title={notification.title}
        content={notification.content}
        open={notification.show}
        close={notification.close}
        bgWhite
      />
      <RetryTransferDialog
        open={retryDialogOpen}
        transferData={retryTransferData}
        setNotification={setNotification}
        onClose={handleDialogClose}
      />
    </>
  );
};

export default TransactionsTable;
